import React, { useEffect, useCallback, useState, useMemo } from 'react';
import AppLayout from '../../../../component/appLayout';
import {
  Group,
  PendingButton,
  Header,
  PersonalText,
  PersonInfo,
  ProjectContent,
  Teammate,
  TeammateName,
  Pending,
  AdminText,
  RightDiv,
  Container
} from '../../styles';
import { getWorkspaceDetails } from '../../../../services/workspaceService';
import Avatar from '../../../../component/avatar';
import { isEmpty } from '../../../../helpers/common';
import { changeUserAdmin, deleteInvitedUser } from '../../../../services/settingServices';
import DeleteModal from '../../../../component/modals/deleteModal';
import Button from '../../../../component/button';
import Modal from '../../../../component/modals/modal';
import InviteTeamsModal from '../../../../component/modals/inviteTeamsModal';
import {
  AVATAR_BORDER,
  BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  SETTING_TEAMS_MENU_OPTION,
  USER_ROLE
} from '../../../../global/constants';
import { captureException } from '../../../../services/logService';
import SuccessfulModel from '../../../../component/modals/successfullModal';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import TeamsLoading from '../../../../component/loading/teamsLoading';
import { UsersWorkspaceInterface } from '../../../../interfaces/WorkspaceInterface';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import IconButton from '../../../../component/button/iconButton';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { InvitedUser } from '../../../../mobx/interfaces/user';
import { ActiveDiv, Details, Dot, DotIcon } from './styles';

const getLastSeenText = (lastSeenAt?: string | Date): React.ReactNode => {
  if (!lastSeenAt) return '';
  const lastSeen = new Date(lastSeenAt);
  const now = new Date();
  const diffMs = now.getTime() - lastSeen.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1)
    return (
      <ActiveDiv>
        <p>Online</p>
        <DotIcon isActive>
          <Dot isActive />
        </DotIcon>
      </ActiveDiv>
    );
  if (diffMinutes < 60) return `Last seen ${diffMinutes} min${diffMinutes > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `Last seen ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `Last seen ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

const Teams: React.FC = () => {
  // States
  const [selectedItem, setSelectedItem] = useState<UsersWorkspaceInterface | InvitedUser | undefined>();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSuccessfulModalOpen, setIsSuccessfulModalOpen] = useState(false);
  const [clientLoginUrl, setClientLoginUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // mobx states
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const isMobile = useMobileDevice();

  const handleOnClick = () => {
    setIsModalOpen(true);
  };

  const handleOnclickOpenModal = () => {
    setIsSuccessfulModalOpen(true);
  };

  const handleOnclickCloseModal = () => {
    setIsSuccessfulModalOpen(false);
  };

  const handleCloseModal = (clientUrl?: string) => {
    setIsModalOpen(false);
    if (clientUrl) {
      setClientLoginUrl(clientUrl);
      handleOnclickOpenModal();
    }
  };

  const loadData = useCallback(async () => {
    const workspaceId = rootStore?.workspaceStore?.currentWorkspace?.id;
    if (workspaceId && !isEmpty(workspaceId)) await getWorkspaceDetails(workspaceId, { includeLastSeen: true });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openDeleteModal = useCallback(
    (item: UsersWorkspaceInterface | InvitedUser) => {
      setDeleteModal(true);
      setSelectedItem(item);
    },
    [setSelectedItem]
  );

  const close = useCallback(() => {
    setDeleteModal(false);
    setSelectedItem(undefined);
  }, [setSelectedItem]);

  const onClickDelete = useCallback(async () => {
    try {
      setDeleteLoading(true);
      const result = await deleteInvitedUser(selectedItem?.id);
      if (result) {
        close();
        loadData();
      }
    } catch (error) {
      captureException(error);
      setDeleteLoading(false);
    } finally {
      setDeleteLoading(false);
    }
  }, [selectedItem, close, loadData]);

  const onClickAdmin = useCallback(
    async (userId: string) => {
      const result = await changeUserAdmin(userId);
      if (result) {
        loadData();
      }
    },
    [loadData]
  );

  const getMenuItems = (user: { isAdmin: boolean }) => [
    { key: SETTING_TEAMS_MENU_OPTION.ADMIN, label: user.isAdmin ? 'Remove Admin' : 'Make Admin' },
    { key: SETTING_TEAMS_MENU_OPTION.DELETE, label: 'Delete' }
  ];
  const handleClick = useCallback(
    (key: number | string, user: UsersWorkspaceInterface) => {
      key = Number(key);
      if (key === SETTING_TEAMS_MENU_OPTION.ADMIN) {
        onClickAdmin(user?.id);
      } else if (key === SETTING_TEAMS_MENU_OPTION.DELETE) {
        openDeleteModal(user);
      }
    },
    [openDeleteModal, onClickAdmin]
  );

  const renderMoreIconButton = useMemo(() => {
    return (
      <IconButton
        iconName='three-dots-horizontal-icon'
        size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.SMALL}
        variant={ICON_BUTTON_TYPE.TERTIARY}
      />
    );
  }, [isMobile]);

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form'>
            {!isMobile ? (
              <Header>
                <PersonalText>Teams</PersonalText>
                <Button
                  title='Invite'
                  variant={BUTTON_TYPE.PRIMARY}
                  buttonType='button'
                  onClick={handleOnClick}
                  size='small'
                />
              </Header>
            ) : (
              <ResponsiveHeader title='Teams' />
            )}
            {isEmpty(currentWorkspace) && <TeamsLoading />}

            {!isEmpty(currentWorkspace) && (
              <ProjectContent>
                {currentWorkspace?.users?.map((user, index) => {
                  if (user?.role !== USER_ROLE.CLIENT && !user?.id?.startsWith('agent_'))
                    return (
                      <Teammate key={user?.id}>
                        <PersonInfo>
                          <Avatar
                            imgSrc={user?.profile_photo || ''}
                            name={user?.name}
                            size={32}
                            borderVariant={AVATAR_BORDER.NONE}
                          />
                          <TeammateName>
                            <h1>{user?.name}</h1>
                            <Details>
                              <p>{user?.email}</p>
                              {user?.lastSeen && (
                                <>
                                  <DotIcon>
                                    <Dot />
                                  </DotIcon>
                                  <p>{getLastSeenText(user?.lastSeen)}</p>
                                </>
                              )}
                            </Details>
                          </TeammateName>
                        </PersonInfo>
                        <RightDiv>
                          {user?.isOwner ? (
                            <AdminText>Owner</AdminText>
                          ) : user?.isAdmin ? (
                            <AdminText>Admin</AdminText>
                          ) : (
                            ''
                          )}
                          <Dropdown
                            modalTitle='Manage Member'
                            activeClassName='active'
                            content={renderMoreIconButton}
                            trigger='click'>
                            {getMenuItems(user).map((item) => (
                              <DropdownItem
                                key={item.key}
                                label={item.label}
                                onClick={() => handleClick(item.key, user as UsersWorkspaceInterface)}
                                tone={item.label === 'Delete' ? 'critical' : 'primary'}
                              />
                            ))}
                          </Dropdown>
                        </RightDiv>
                      </Teammate>
                    );
                  else return <React.Fragment key={user?.id}></React.Fragment>;
                })}
                {currentWorkspace?.invitedUsers?.map((user, index) => {
                  if (user?.role !== USER_ROLE.CLIENT)
                    return (
                      <Teammate key={user?.id}>
                        <PersonInfo className='pending'>
                          <Avatar
                            imgSrc={''}
                            name={!isEmpty(user?.name) ? user?.name : 'U'}
                            size={32}
                            borderVariant={AVATAR_BORDER.NONE}
                          />
                          <TeammateName>
                            <h1>{user?.name}</h1>
                            <p>{user?.email}</p>
                          </TeammateName>
                        </PersonInfo>
                        <Pending>
                          <PendingButton>Pending</PendingButton>
                          <Dropdown
                            modalTitle='Manage Member'
                            activeClassName='active'
                            content={renderMoreIconButton}
                            trigger='click'>
                            <DropdownItem
                              label='Delete'
                              onClick={() => {
                                openDeleteModal(user);
                              }}
                              tone='critical'
                            />
                          </Dropdown>
                        </Pending>
                      </Teammate>
                    );
                  else return <React.Fragment key={user?.id}></React.Fragment>;
                })}
              </ProjectContent>
            )}
          </Group>
        </Container>
      </AppLayout>
      <Modal open={isModalOpen} onClose={handleCloseModal} maxWidth={'400px'}>
        <InviteTeamsModal onCancel={handleCloseModal} workspace_id={currentWorkspace?.id || ''} />
      </Modal>
      <Modal open={isSuccessfulModalOpen} onClose={handleOnclickCloseModal} maxWidth={'420px'}>
        <SuccessfulModel isFromTeam={true} clientLoginUrl={clientLoginUrl} onCancel={handleOnclickCloseModal} />
      </Modal>
      <Modal open={deleteModal} onClose={close} maxWidth={'334px'}>
        <DeleteModal
          onClose={close}
          onOk={onClickDelete}
          loading={deleteLoading}
          modalTitle='Delete Team member?'
          description='Are you sure you want to delete this team member?'
        />
      </Modal>
      <BottomSubNavigation
        rightElement={<BottomNavigationItem iconName='member-plus-icon' onClick={handleOnClick} />}
      />
    </>
  );
};

export default observer(Teams);
